import { toasterState } from "Components/common/toaster";
import DriverMaster from "Components/driver/Driver";
import {
  AUTH_CLIENT_ID,
  AUTH_REDIRECT_URL,
  AUTH_SCOPES,
  AUTH_SCOPES_CALENDAR,
  OUTLOOK_CLIENT_ID,
  OUTLOOK_REDIRECT_URL,
} from "Static/constant";
import userDetail from "Stores/userDetail";
import InboxUtils from "Utils/InboxUtils";
import PaywallUtils from "Utils/PaywallUtils";
import Utils from "Utils/utils";
import connectedInboxStore from "../ConnectInbox/store";
import { gmailMessagePopupState } from "../ConnectInbox/GmailMessagePopup";
import reconnectSequencePopupState from "../ConnectInbox/ReconnectSequencePopupState";

const connectedEmailUtils = {};

const handleGoogleOauthCbk = async (cbkCode = "", inboxId = "", connectedInbox = {}) => {
  const { noOfInboxesUsed = 0 } = userDetail?.userFeatureFlag || {};

  switch (cbkCode) {
    case "200":
      connectedInboxStore.setLoading(true);
      userDetail.setUserFeatureFlag({
        ...userDetail.userFeatureFlag,
        noOfInboxesUsed: noOfInboxesUsed + 1
      });
      await connectedInboxStore.getAccountDetails(true);
      window.postMessage({ type: "CONNECT_INBOX_SUCCESS" }, "*");
      Utils.getOnboardingData();
      Utils.trackOnboardingMixpanel("account_connected");
      if (inboxId && Object.keys(connectedInbox).length > 0) {
        await reconnectSequencePopupState.getAllCampaignSequences(inboxId, connectedInbox);
      }
      break;
    case "400":
      gmailMessagePopupState.setPopupValues({
        title: "Insufficient Permission",
        subTitle: `Unable to connect your Google account with SalesIntel due to insufficient permissions.
      Please retry connecting by enabling all the required permissions on the consent screen 
      <br>(Please refer to the video below)`
      });
      gmailMessagePopupState.setShowPopup(true);
      break;
    case "409":
      gmailMessagePopupState.setPopupValues({
        title: "Connection Failed",
        subTitle: `Unable to connect your Google account with SalesIntel as its already connected to a different SalesIntel account. Please try re-connecting a different Google account`,
        additionalLink: `<a class="errorLink" href="https://support.salesgear.io/en/articles/5660382-why-do-i-get-the-connection-failed-error-when-i-try-to-connect-my-gmail-with-salesgear" target="_blank" rel="noopener noreferrer"><span>Why do I get the connection failed error?</span><i class="fa fa-external-link openInNewTabIcon" aria-hidden="true"></i></a>`
      });
      gmailMessagePopupState.setShowPopup(true);
      break;
    case "412":
      gmailMessagePopupState.setPopupValues({
        title: "Connection Failed",
        subTitle: `Please connect the same email account`
      });
      gmailMessagePopupState.setShowPopup(true);
      break;
    case "413":
      toasterState.setToastMsg(
        `Oops! You cannot connect personal email addresses.`,
        "fail"
      );
      break;
    case "500":
    default:
      toasterState.setToastMsg(
        "Something went wrong. Please try again later",
        "fail"
      );
  }
};

const closeGmailMessagePopup = () => {
  gmailMessagePopupState.setPopupValues({});
  gmailMessagePopupState.setShowPopup(false);
};

connectedEmailUtils.openGmailConnectPopup = () => {
  if (InboxUtils.connectInboxAllowed()) {
    DriverMaster.killDriver();
    gmailMessagePopupState.setConnectBtnAction(
      connectedEmailUtils.openGoogleAuth
    );
    // gmailMessagePopupState.setPopupValues({
    //   title: `Connect your Google account with Salesgear`,
    //   subTitle: ""
    // });
    gmailMessagePopupState.setShowPopup(true);
  } else {
    upgradeMulitpleInbox();
  }
};

connectedEmailUtils.openGoogleAuth = async () => {
  const { calendarEnabled = false } = userDetail?.userInfo || {};
  const { calendarIntegrationEnabled = false } =
    userDetail?.userFeatureFlag || {};
  const {
    gmailClientId = "",
    gmailRedirectUrl = "",
    gmailScopes = ""
  } = connectedInboxStore.oAuthConfigs || {};

  closeGmailMessagePopup();
  let gmailAuthScope =
    calendarEnabled && calendarIntegrationEnabled
      ? AUTH_SCOPES_CALENDAR
      : gmailScopes;
  const authUrl =
    "https://accounts.google.com/o/oauth2/auth?client_id=" +
    gmailClientId +
    "&scope=" +
    gmailAuthScope +
    "&access_type=offline&response_type=code&redirect_uri=" +
    gmailRedirectUrl +
    "&prompt=select_account%20consent" +
    "&hd=*";
  // The app runs inside an iframe (Shell.js); after the popup round-trips
  // through Google, Chrome severs `window.opener`, so the popup can't close
  // itself and plain postMessage gets dropped. openConnectPopup closes the
  // popup from here and receives the result over the multi-channel bridge.
  const { openConnectPopup } = require("Pages/Dashboard/connectApps/oauthPopupBridge");
  openConnectPopup({
    url: authUrl,
    types: ["GOOGLE_CONNECT_INBOX"],
    onResult: (data) =>
      handleGoogleOauthCbk(data?.code?.toString(), data?.inboxId, data?.connectedInbox)
  });
};

connectedEmailUtils.microsoftAuth = async () => {
  const { noOfInboxesUsed = 0 } = userDetail?.userFeatureFlag || {};
  const {
    outlookClientId = "",
    outlookRedirectUrl = "",
    outlookScopes = ""
  } = connectedInboxStore.oAuthConfigs || {};

  if (InboxUtils.connectInboxAllowed()) {
    DriverMaster.killDriver();
    const authUrl =
      "https://login.microsoftonline.com/common/oauth2/v2.0/authorize?" +
      "client_id=" +
      outlookClientId +
      "&response_type=code" +
      "&redirect_uri=" +
      outlookRedirectUrl +
      "&response_mode=query" +
      "&scope=" +
      outlookScopes +
      "&state=12345" +
      "&prompt=select_account";
    // Popup can't self-close inside the Shell iframe (COOP severs opener);
    // openConnectPopup closes it from here and delivers the result.
    const { openConnectPopup } = require("Pages/Dashboard/connectApps/oauthPopupBridge");
    openConnectPopup({
      url: authUrl,
      types: ["MICROSOFT_CONNECT_INBOX"],
      onResult: async (msg = {}) => {
        const { code = "", inboxId = "", connectedInbox = {} } = msg;
        if (code === "200" || code === 200) {
          userDetail.setUserFeatureFlag({
            ...userDetail.userFeatureFlag,
            noOfInboxesUsed: noOfInboxesUsed + 1,
          });
          connectedInboxStore.setLoading(true);
          await connectedInboxStore.getAccountDetails(true);
          Utils.getOnboardingData();
          Utils.trackOnboardingMixpanel("account_connected");
          if (inboxId && Object.keys(connectedInbox).length > 0) {
            await reconnectSequencePopupState.getAllCampaignSequences(inboxId, connectedInbox);
          }
        } else if (code === "409" || code === 409) {
          setTimeout(() => {
            toasterState.setToastMsg(
              `Failed. This inbox is already connected to a different SalesIntel account. Try with a different one.`,
              "fail"
            );
          }, 200);
        } else {
          toasterState.setToastMsg(
            "Something went wrong. Please try again later",
            "fail"
          );
        }
      }
    });
  } else {
    upgradeMulitpleInbox();
  }
};

const upgradeMulitpleInbox = () => {
  const { planName = "", inFreePlan = false } =
    userDetail?.userFeatureFlag || {};
  let tempPlanName = planName?.toLowerCase();
  let inboxModel =
    inFreePlan || ["starter", "basic"].includes(tempPlanName)
      ? "multipleInbox"
      : ["pro", "unlimited", "growth"].includes(tempPlanName)
        ? "multipleInboxCustom"
        : "";

  inboxModel &&
    PaywallUtils.upgradeUtilsFunction(inboxModel, false, planName, inFreePlan);
};

export { connectedEmailUtils };
